import { useEffect, useState } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import type { FieldValues, SubmitHandler } from 'react-hook-form';

import Heading from 'components/partials/heading/heading';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Loader from 'components/partials/loader/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import ClientForm from 'containers/admin/clients/client/forms/client-form';
import TransitionUsersModal from 'containers/admin/clients/client/transition-users-modal/transition-users-modal';

import {
  useCreateOrUpdateClient,
  useClientIdentityProviderTransition,
} from 'hooks/api/use-clients';
import { useReadOnly } from 'hooks/read-only';

import { transformProfileFormData } from 'helpers/forms';
import type { ProfileFormValues } from 'helpers/forms';

interface ClientProfileProps {
  pageName?: string;
  isFormDisabled?: boolean;
  setIsFormDisabled?: (value: boolean) => void;
  showClientDeleteBtn?: boolean;
  defaultValues?: Partial<ProfileFormValues> | Record<string, unknown>;
}

const DEFAULT_VALUES: Record<string, unknown> = {
  name: '',
  authorizedDomains: [],
  externalId: '',
  state: '',
  identityProviderId: '',
  expireDate: '01/01/2100',
};

const ClientProfile = ({
  pageName = 'Profile information',
  isFormDisabled = false,
  setIsFormDisabled,
  showClientDeleteBtn,
  defaultValues = DEFAULT_VALUES,
}: ClientProfileProps): React.JSX.Element => {
  const navigate = useNavigate();
  const readOnly = useReadOnly();
  const createOrUpdateClient = useCreateOrUpdateClient();
  const {
    pendingTransition,
    mutation: pendingTransitionMutation,
    isTransitionPending,
    startTransition,
    detectTransitionFromIdentityProviderChange,
    clearPendingTransition,
  } = useClientIdentityProviderTransition();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    clearErrors,
  } = useForm<FieldValues>({
    mode: 'onChange',
    defaultValues: defaultValues as FieldValues,
  });

  const [savedIdentityProviderId, setSavedIdentityProviderId] = useState(
    String(defaultValues.identityProviderId ?? '')
  );

  useEffect(() => {
    if (defaultValues.id) {
      reset(defaultValues as FieldValues);
      setSavedIdentityProviderId(
        String(defaultValues.identityProviderId ?? '')
      );
    }
  }, [defaultValues.id, reset]);

  const loading = createOrUpdateClient.isPending;

  const onSubmit: SubmitHandler<FieldValues> = (formData) => {
    const dataToSend = transformProfileFormData(
      formData as ProfileFormValues
    ) as { id?: string | number; name?: string } & Record<string, unknown>;

    const nextIdentityProviderId = String(dataToSend.identityProviderId ?? '');

    createOrUpdateClient.mutate(dataToSend, {
      onSuccess: () => {
        reset(formData);

        if (setIsFormDisabled) {
          detectTransitionFromIdentityProviderChange(
            savedIdentityProviderId,
            nextIdentityProviderId
          );
          setSavedIdentityProviderId(nextIdentityProviderId);
          setIsFormDisabled(true);
        } else {
          navigate('/admin/clients');
        }
      },
    });
  };

  const handleCancel = (): void => {
    reset();

    if (setIsFormDisabled) {
      setIsFormDisabled(true);
    } else {
      navigate('/admin/clients');
    }
  };

  return (
    <ResponsiveWrapper>
      <Loader {...{ loading }} />
      {!showClientDeleteBtn && (
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Clients', href: '/admin/clients' },
            { label: 'New client', isActive: true },
          ]}
        />
      )}
      <div className="flex flex-col w-full mt-8">
        <div className="flex items-center justify-between">
          <Heading
            variant={!!showClientDeleteBtn ? 'h2' : 'h1'}
            customClass="mb-8"
          >
            {pageName}
          </Heading>

          {!!showClientDeleteBtn && setIsFormDisabled && (
            <ActionMenu
              onEditClick={() => setIsFormDisabled(false)}
              isActionMenuDisabled={!isFormDisabled}
              options={[
                ...(!readOnly
                  ? [
                      savedIdentityProviderId
                        ? {
                            label: 'Transition users to SSO',
                            onClick: () => startTransition('toSso'),
                            disabled: isTransitionPending,
                          }
                        : {
                            label: 'Transition users to Okta',
                            onClick: () => startTransition('toOkta'),
                            disabled: isTransitionPending,
                          },
                    ]
                  : []),
              ]}
            />
          )}
        </div>

        <ClientForm
          {...{
            isFormDisabled,
            isSaving: loading,
            onCancel: handleCancel,
            control,
            onSubmit,
            handleSubmit,
            errors,
            clearErrors,
          }}
        />
      </div>

      {pendingTransition && (
        <TransitionUsersModal
          direction={pendingTransition}
          clientId={Number(defaultValues.id)}
          mutation={pendingTransitionMutation}
          onCloseCallback={clearPendingTransition}
        />
      )}
    </ResponsiveWrapper>
  );
};

export default ClientProfile;
